import { Injectable, OnDestroy } from '@angular/core';
import { LoggerStorageService } from '@app/phone/services/logger-storage.service';
import { ElectronChannel, MainProcessLogEntry, MainProcessLogLevel } from '@shared/types';
import { Observable, Subject } from 'rxjs';

import { originalConsoleMethods } from '../core/utils/renderer-console-interceptor';
import { ElectronService } from './electron.service';

/**
 * Service that receives console logs from the Electron main process
 * and outputs them to the renderer console with visual distinction.
 *
 * Only active in development mode when running in Electron.
 */
@Injectable({
  providedIn: 'root',
})
export class MainProcessLogService implements OnDestroy {
  private readonly logSubject = new Subject<MainProcessLogEntry>();

  /** Observable stream of main process logs */
  public readonly logs$: Observable<MainProcessLogEntry> = this.logSubject.asObservable();

  constructor(
    private electronService: ElectronService,
    private loggerStorageService: LoggerStorageService
  ) {
    this.initLogSubscription();
  }

  /**
   * Initialize the log subscription if running in Electron.
   */
  private initLogSubscription(): void {
    // TODO: Re-enable production check after testing
    // if (environment.production || !this.electronService.isRunningInElectron) {
    if (!this.electronService.isRunningInElectron) {
      return;
    }

    this.electronService.on(ElectronChannel.MainProcessLog, (logEntry) => {
      this.logSubject.next(logEntry);
      this.outputToRendererConsole(logEntry);

      // Store to IndexedDB with clean format (no CSS styling)
      const message = `[MAIN ${logEntry.timestamp}] ${logEntry.message}${logEntry.args.length ? ' ' + logEntry.args.join(' ') : ''}`;
      this.loggerStorageService.addLog(logEntry.level, message);
    });
  }

  /**
   * Outputs main process logs to the renderer console with visual distinction.
   * Uses original console methods to avoid double-prefixing from the renderer interceptor.
   */
  private outputToRendererConsole(entry: MainProcessLogEntry): void {
    const prefix = `[MAIN ${entry.timestamp}]`;
    const style = this.getLogStyle(entry.level);

    // Use original console methods to avoid double-prefixing
    const consoleMethod = originalConsoleMethods[entry.level] || originalConsoleMethods.log;

    if (entry.args.length > 0) {
      consoleMethod.call(console, `%c${prefix}`, style, entry.message, ...entry.args);
    } else {
      consoleMethod.call(console, `%c${prefix}`, style, entry.message);
    }
  }

  /**
   * Returns CSS style for the log prefix based on log level.
   */
  private getLogStyle(level: MainProcessLogLevel): string {
    const baseStyle = 'font-weight: bold; padding: 2px 4px; border-radius: 2px;';

    switch (level) {
      case 'error':
        return `${baseStyle} background: #ff4444; color: white;`;
      case 'warn':
        return `${baseStyle} background: #ffaa00; color: black;`;
      case 'debug':
        return `${baseStyle} background: #9944ff; color: white;`;
      case 'info':
        return `${baseStyle} background: #4488ff; color: white;`;
      default: // 'log'
        return `${baseStyle} background: #44aa44; color: white;`;
    }
  }

  public ngOnDestroy(): void {
    this.logSubject.complete();
  }
}
